import type {ReactElement} from 'react';
import {useRef} from 'react';
import {PANEL_SMALL_CONTAINER_QUERY} from '@Components/panel/panel.types';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import {PanelBottomPills} from '@Components/panel/components/panel-bottom-pills';
import styles from './panel-footer.module.scss';
import type {PanelFooterPrimaryActionType} from '../panel-footer-primary-actions';
import {PanelFooterPrimaryActions} from '../panel-footer-primary-actions';
import type {PanelFooterSecondaryActionType} from '../panel-footer-secondary-actions';
import {PanelFooterSecondaryActions} from '../panel-footer-secondary-actions';

export interface PanelFooterProps {
  className?: string;
  info?: string;
  infoPosition?: FooterInfoPosition;
  border?: boolean;
  fullWidthPrimaryActions?: boolean;
  actionsContainerClassName?: string;
  primaryActionsClassName?: string;
  secondaryActionsClassName?: string;
  primaryActions?: PanelFooterPrimaryActionType[];
  secondaryActions?: PanelFooterSecondaryActionType[];
  bottomPills?: PanelBottomPill[];
  isScrollable?: boolean;
  hasFullWidthActions?: boolean;
  primaryActionsOnTop?: boolean;
  spacingBetweenActionsClassname?: string;
  /** Panel (mobile) mode stretches each action to fill the row; false keeps them at natural width. */
  shouldStretchActionsInPanelMode?: boolean;
}

enum FooterInfoPosition {
  LEFT = 'flex-vertical-start',
  RIGHT = 'flex-vertical-end',
  CENTER = 'flex-vertical-center',
}

export function PanelFooter({
  className = '',
  info = '',
  infoPosition = FooterInfoPosition.LEFT,
  primaryActionsClassName = '',
  secondaryActionsClassName = '',
  fullWidthPrimaryActions = false,
  actionsContainerClassName = '',
  border = !fullWidthPrimaryActions,
  primaryActions = [],
  secondaryActions = [],
  bottomPills = [],
  isScrollable = false,
  hasFullWidthActions = false,
  primaryActionsOnTop = false,
  spacingBetweenActionsClassname = 'spacing-m-t-3',
  shouldStretchActionsInPanelMode = true,
}: PanelFooterProps): ReactElement<any> {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, PANEL_SMALL_CONTAINER_QUERY);

  const getInfoContainer = (): ReactElement<any> => {
    return <div className={`body-xs-italic spacing-p-t-2 ${infoPosition}`}>{info}</div>;
  };

  const getPrimaryAction = (): ReactElement<any> => {
    return (
      <PanelFooterPrimaryActions className={`${primaryActionsClassName} ${styles.actions}`} fullWidth={fullWidthPrimaryActions || hasFullWidthActions} actions={primaryActions} />
    );
  };

  const getSecondaryAction = (): ReactElement<any> | null => {
    if (!secondaryActions.length) {
      return null;
    }
    return <PanelFooterSecondaryActions className={`${secondaryActionsClassName} ${styles.actions}`} fullWidth={hasFullWidthActions} actions={secondaryActions} />;
  };

  const getPanelActions = (): ReactElement<any> | null => {
    if (!hasFullWidthActions) {
      return (
        <>
          {fullWidthPrimaryActions ? null : getSecondaryAction()}
          {getPrimaryAction()}
        </>
      );
    }

    return (
      <div className="flex-v-row _full-width">
        {primaryActionsOnTop ? getPrimaryAction() : getSecondaryAction()}
        <div className={spacingBetweenActionsClassname} />
        {primaryActionsOnTop ? getSecondaryAction() : getPrimaryAction()}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`flex-center flex-v-row ${className} ${styles.footer} ${border ? styles.footerBorder : ''} ${params.small ? styles.small : ''}`}>
      {primaryActions?.length || secondaryActions?.length ? (
        <div
          className={`flex-justify-end ${styles.actionsContainer} ${params.small ? `spacing-p-3 ${shouldStretchActionsInPanelMode ? styles.panelModeActions : ''}` : ''} ${actionsContainerClassName}`}
        >
          {getPanelActions()}
        </div>
      ) : null}
      {bottomPills.length ? (
        <PanelBottomPills className={primaryActions?.length || secondaryActions?.length ? styles.footerBorder : ''} pills={bottomPills} isScrollable={isScrollable} />
      ) : null}
      {info !== '' ? getInfoContainer() : null}
    </div>
  );
}
